import { useEffect, useMemo, useState } from "react";
import { Info, X } from "lucide-react";
import toast from "react-hot-toast";
import DatePicker from "../../../Common/DatePicker";
import {
  RequestFormFooter,
  RequestHistoryOverlay,
  useEssRequestHistory,
  DS_QUICK_FIELD_CLS,
  PHONE_SHEET_BACKDROP_CLS,
  PHONE_SHEET_PANEL_CLS,
  usePhoneFormBack,
} from "./RequestHistoryOverlay";
import {
  employeeSeparationService,
  reasonsService,
} from "../../../../services/api";

function addDaysIso(isoDate, days) {
  if (!isoDate) return "";
  const d = new Date(`${isoDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return "";
  d.setDate(d.getDate() + Number(days || 0));
  return d.toISOString().slice(0, 10);
}

function daysBetween(fromIso, toIso) {
  if (!fromIso || !toIso) return 0;
  const a = new Date(`${fromIso}T00:00:00`);
  const b = new Date(`${toIso}T00:00:00`);
  if (Number.isNaN(a.getTime()) || Number.isNaN(b.getTime())) return 0;
  return Math.max(0, Math.ceil((b.getTime() - a.getTime()) / (1000 * 3600 * 24)));
}

function formatMoney(n) {
  if (n == null || !Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(n);
}

/**
 * Self-service / Request-desk / Admin Actions resignation form driven by Separation Policy.
 */
export default function ResignationRequestFormModal({
  darkMode = false,
  isOpen,
  employeeId,
  onClose,
  onSubmitted,
  title = "New Resignation",
}) {
  const today = new Date().toISOString().slice(0, 10);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [reasons, setReasons] = useState([]);
  const [preview, setPreview] = useState(null);
  const [infoOpen, setInfoOpen] = useState(false);
  const [form, setForm] = useState({
    resignationDate: today,
    lastWorkingDate: "",
    reasonId: "",
    remarks: "",
  });
  const [reasonsLoaded, setReasonsLoaded] = useState(false);

  const inputCls = DS_QUICK_FIELD_CLS;
  const labelCls = "block text-xs font-medium text-[hsl(var(--ds-muted-fg))]";
  const mutedBox =
    "rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted)/0.35)] px-3 py-2 text-sm text-[hsl(var(--ds-fg))]";
  const requestHistory = useEssRequestHistory({
    enabled: isOpen,
    actionLabel: "Resignation",
    employeeId,
  });

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      resignationDate: today,
      lastWorkingDate: "",
      reasonId: "",
      remarks: "",
    });
    setPreview(null);
    setReasonsLoaded(false);
    setInfoOpen(false);
  }, [isOpen, today]);

  useEffect(() => {
    if (!isOpen || reasonsLoaded) return;
    let cancelled = false;
    (async () => {
      try {
        const reasonsRes = await reasonsService.getAllResignationReasons().catch(() => ({ data: [] }));
        if (!cancelled) {
          setReasons(Array.isArray(reasonsRes?.data) ? reasonsRes.data : []);
          setReasonsLoaded(true);
        }
      } catch {
        if (!cancelled) setReasonsLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, reasonsLoaded]);

  useEffect(() => {
    if (!isOpen || !employeeId || !form.resignationDate) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const policyRes = await employeeSeparationService.resolvePolicy(employeeId, {
          separationDate: form.resignationDate,
        });
        if (cancelled) return;
        const data = policyRes?.data || null;
        setPreview(data);
        const suggested =
          data?.suggestedLastWorkingDay ||
          addDaysIso(form.resignationDate, data?.noticePeriodDays || 30);
        setForm((prev) => ({
          ...prev,
          lastWorkingDate: suggested,
        }));
      } catch (err) {
        if (!cancelled) {
          toast.error(
            err?.response?.data?.message || err.message || "Failed to load resignation policy"
          );
          setPreview(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, employeeId, form.resignationDate]);

  const noticeDays = Number(preview?.noticePeriodDays || 30);
  const noticeEndDate = useMemo(
    () => addDaysIso(form.resignationDate, noticeDays),
    [form.resignationDate, noticeDays]
  );
  const noticeDuration = daysBetween(form.resignationDate, form.lastWorkingDate);
  const shortfallDays = Math.max(0, noticeDays - noticeDuration);
  const shortfallAmount =
    preview?.dailyRate != null && shortfallDays > 0
      ? Math.round(preview.dailyRate * shortfallDays)
      : null;
  const checklist = Array.isArray(preview?.checklistPreview) ? preview.checklistPreview : [];
  const policyName = preview?.policy?.name || preview?.policy?.policyName || null;
  const allowsRetro =
    !!preview?.policy?.allowResignationsPriorToCurrentDate ||
    !!preview?.policy?.allowBackdatedResignation;

  const infoLines = useMemo(() => {
    const lines = [];
    if (!policyName) {
      lines.push(
        "No matching Separation Policy is assigned. HR must activate and assign a policy before resignation can be submitted."
      );
    } else {
      lines.push(`Applicable policy: ${policyName}.`);
    }
    if (allowsRetro) {
      lines.push(
        "This policy allows backdated resignation dates (before today). Choose a resignation date on or after the employee joining date."
      );
    } else {
      lines.push("Backdated resignation is not allowed by the applicable policy.");
    }
    if (shortfallDays > 0) {
      lines.push(
        `Relieving before the policy notice end creates a shortfall of ${shortfallDays} day(s)${
          shortfallAmount != null
            ? ` (est. ${formatMoney(shortfallAmount)} from CTC ÷ 30)`
            : ""
        }. Final recovery is confirmed during Full & Final settlement.`
      );
    } else {
      lines.push(
        "Last working date on or after the policy notice end means no notice shortfall."
      );
    }
    if (checklist.length === 0) {
      lines.push(
        "No employee checklist tasks are mapped on this policy. Clearance may still apply after approval."
      );
    } else {
      checklist.forEach((item) => {
        const meta = [
          item.mandatory ? "Mandatory" : "Optional",
          item.department,
          item.actionRole,
        ]
          .filter(Boolean)
          .join(" · ");
        const due = item.dueDateDays ? ` · due in ~${item.dueDateDays}d after raise` : "";
        lines.push(
          `${item.name}: ${meta}${due} — complete when assigned after submission.`
        );
      });
    }
    lines.push("Remarks are optional notes for approvers.");
    return lines;
  }, [
    allowsRetro,
    checklist,
    policyName,
    shortfallAmount,
    shortfallDays,
  ]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!employeeId) {
      toast.error("Employee profile is required to submit resignation.");
      return;
    }
    if (!form.resignationDate || !form.lastWorkingDate || !form.reasonId) {
      toast.error("Please fill resignation date, last working date, and reason.");
      return;
    }
    if (form.lastWorkingDate < form.resignationDate) {
      toast.error("Last working date cannot be before resignation date.");
      return;
    }
    if (!allowsRetro && form.resignationDate < today) {
      toast.error("Backdated resignation is not allowed by the applicable policy.");
      return;
    }
    if (!preview?.policy) {
      toast.error("No separation policy applies. Contact HR.");
      return;
    }

    setSubmitting(true);
    try {
      const reasonRow = reasons.find((r) => String(r.id) === String(form.reasonId));
      const response = await employeeSeparationService.createRequest({
        employeeId: Number(employeeId),
        separationType: "resignation",
        resignationDate: form.resignationDate,
        separationDate: form.resignationDate,
        relievingDate: form.lastWorkingDate,
        lastWorkingDay: form.lastWorkingDate,
        reasonId: form.reasonId,
        reason: reasonRow?.name || "",
        remarks: form.remarks || "",
        noticePeriodDays: noticeDays,
        noticePeriodWaiverDays: 0,
        noticePeriodToDeduct: shortfallDays,
        noticeBuyoutAmount: shortfallAmount,
        submit: true,
      });

      if (response?.success !== false) {
        toast.success(
          response?.data?.status === "completed" || response?.data?.status === "approved"
            ? "Resignation submitted"
            : "Resignation submitted for approval"
        );
        window.dispatchEvent(new CustomEvent("admin:lifecycle-request-changed"));
        onSubmitted?.(response?.data);
        onClose?.();
      } else {
        toast.error(response?.message || "Failed to submit resignation");
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err.message || "Failed to submit resignation");
    } finally {
      setSubmitting(false);
    }
  };

  usePhoneFormBack(isOpen, onClose);

  if (!isOpen) return null;

  void darkMode;

  const headerSubtitle = preview?.employee?.name
    ? `${preview.employee.name}${preview.employee.empCode ? ` · ${preview.employee.empCode}` : ""}`
    : null;

  return (
    <>
      <div className={`fixed inset-0 z-[10030] flex items-center justify-center bg-black/50 p-4 ${PHONE_SHEET_BACKDROP_CLS}`}>
        <div className={`flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-2xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)] ${PHONE_SHEET_PANEL_CLS}`}>
          <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-5 py-4">
            <div>
              <h3 className="ds-h4 text-[hsl(var(--ds-fg))]">
                {headerSubtitle || title}
              </h3>
              {headerSubtitle ? (
                <p className="mt-0.5 text-[11px] text-[hsl(var(--ds-muted-fg))]">{title}</p>
              ) : (
                <p className="mt-0.5 text-[11px] text-[hsl(var(--ds-muted-fg))]">
                  Based on the applicable Separation Policy
                </p>
              )}
            </div>
            <div className="flex items-center gap-1">
              {infoLines.length > 0 && (
                <div className="relative">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setInfoOpen((open) => !open);
                    }}
                    onBlur={() => setInfoOpen(false)}
                    className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[hsl(var(--ds-muted-fg))] transition hover:bg-[hsl(var(--ds-muted))]"
                    aria-label="Resignation form details"
                    aria-expanded={infoOpen}
                  >
                    <Info size={18} strokeWidth={1.75} />
                  </button>
                  {infoOpen ? (
                    <div
                      className="absolute right-0 top-full z-20 mt-1 w-72 rounded-md border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] p-3 text-left text-xs leading-snug text-[hsl(var(--ds-muted-fg))] shadow-lg sm:w-80"
                      role="tooltip"
                      onMouseDown={(e) => e.preventDefault()}
                    >
                      <ul className="space-y-2">
                        {infoLines.map((line) => (
                          <li key={line}>{line}</li>
                        ))}
                      </ul>
                    </div>
                  ) : null}
                </div>
              )}
              <button
                type="button"
                onClick={onClose}
                className="inline-flex h-9 w-9 items-center justify-center rounded-xl text-[hsl(var(--ds-muted-fg))] transition hover:bg-[hsl(var(--ds-muted))]"
                aria-label="Close"
              >
                <X size={14} />
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4">
            {loading ? (
              <p className="text-sm text-[hsl(var(--ds-muted-fg))]">Loading policy…</p>
            ) : (
              <>
                <div className={mutedBox}>
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--ds-muted-fg))]">
                    Applicable policy
                  </div>
                  <div
                    className={`mt-1 text-sm font-medium ${
                      !policyName ? "text-[hsl(var(--ds-danger))]" : ""
                    }`}
                  >
                    {policyName || "No matching separation policy"}
                  </div>
                </div>

                <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <label className={labelCls}>
                    Resignation date <span className="text-[hsl(var(--ds-danger))]">*</span>
                    <DatePicker
                      value={form.resignationDate || ""}
                      min={preview?.employee?.dateOfJoin || undefined}
                      max={allowsRetro ? undefined : today}
                      onChange={(e) => setField("resignationDate", e.target.value)}
                      portal
                      dropdownClassName="z-[10060]"
                      className={inputCls}
                      required
                    />
                  </label>
                  <label className={labelCls}>
                    Last working date <span className="text-[hsl(var(--ds-danger))]">*</span>
                    <DatePicker
                      value={form.lastWorkingDate || ""}
                      min={form.resignationDate || undefined}
                      onChange={(e) => setField("lastWorkingDate", e.target.value)}
                      portal
                      dropdownClassName="z-[10060]"
                      className={inputCls}
                      required
                    />
                  </label>
                </div>

                <label className={labelCls}>
                  Resignation reason <span className="text-[hsl(var(--ds-danger))]">*</span>
                  <select
                    required
                    value={form.reasonId}
                    onChange={(e) => setField("reasonId", e.target.value)}
                    className={inputCls}
                  >
                    <option value="">Select reason</option>
                    {reasons.map((r) => (
                      <option key={r.id} value={r.id}>
                        {r.name}
                      </option>
                    ))}
                  </select>
                </label>

                <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                  <div>
                    <div className="text-[11px] font-medium text-[hsl(var(--ds-muted-fg))]">
                      Policy notice
                    </div>
                    <div className={`${mutedBox} mt-1`}>{noticeDays} days</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium text-[hsl(var(--ds-muted-fg))]">
                      Notice period end
                    </div>
                    <div className={`${mutedBox} mt-1`}>{noticeEndDate || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium text-[hsl(var(--ds-muted-fg))]">
                      Notice shortfall
                    </div>
                    <div
                      className={`${mutedBox} mt-1 ${
                        shortfallDays > 0 ? "font-semibold text-[hsl(var(--ds-warning))]" : ""
                      }`}
                    >
                      {shortfallDays} days
                    </div>
                  </div>
                  <div>
                    <div className="text-[11px] font-medium text-[hsl(var(--ds-muted-fg))]">
                      Shortfall amount
                    </div>
                    <div
                      className={`${mutedBox} mt-1 ${
                        shortfallAmount > 0 ? "font-semibold text-[hsl(var(--ds-warning))]" : ""
                      }`}
                    >
                      {shortfallDays <= 0
                        ? "Nil"
                        : shortfallAmount != null
                          ? formatMoney(shortfallAmount)
                          : "Settled in F&F"}
                    </div>
                  </div>
                </div>

                <div>
                  <div className="mb-1.5 text-xs font-semibold text-[hsl(var(--ds-fg))]">
                    Exit checklist (from policy)
                  </div>
                  {checklist.length === 0 ? (
                    <div className={`${mutedBox} text-[hsl(var(--ds-muted-fg))]`}>None</div>
                  ) : (
                    <ul className="space-y-1.5">
                      {checklist.map((item) => (
                        <li
                          key={item.key}
                          className="flex items-start gap-2 rounded-md border border-[hsl(var(--ds-border))] px-2.5 py-2 text-xs text-[hsl(var(--ds-fg))]"
                        >
                          <span className="mt-0.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--ds-primary))]" />
                          <span className="font-medium">{item.name}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>

                <label className={labelCls}>
                  Remarks
                  <textarea
                    rows={3}
                    maxLength={500}
                    value={form.remarks}
                    onChange={(e) => setField("remarks", e.target.value)}
                    className={inputCls}
                  />
                </label>
              </>
            )}

            <RequestFormFooter
              historyCount={requestHistory.rows.length}
              historyLoading={requestHistory.loading}
              onViewHistory={() => requestHistory.setOpen(true)}
              onCancel={onClose}
              isSubmitting={submitting}
              submitDisabled={loading || !preview?.policy}
              submitLabel="Submit resignation"
            />
          </form>
        </div>
      </div>
      <RequestHistoryOverlay
        open={requestHistory.open}
        onClose={() => requestHistory.setOpen(false)}
        title="Resignation application history"
        subtitle={
          preview?.employee?.name
            ? `${preview.employee.name}${preview.employee.empCode ? ` · ${preview.employee.empCode}` : ""}`
            : "Resignation"
        }
        rows={requestHistory.rows}
      />
    </>
  );
}
